import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Search, MapPin, Users, Star, Zap, ChevronRight, Shield, Globe, ArrowRight, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Player counts used to live here as literal strings ("1,420+", "987+", ...),
// duplicating the fabricated numbers in /api/sports. They now come from that
// endpoint, which counts users. The descriptions below are product copy about
// how matching works, not popularity claims, so they stay.
const SPORT_COPY = {
  tennis:       { name: "Tennis",       desc: "NTRP-matched singles & doubles" },
  pickleball:   { name: "Pickleball",   desc: "Fastest-growing racket sport" },
  golf:         { name: "Golf",         desc: "Connect at your handicap level" },
  "table-tennis": { name: "Table Tennis", desc: "Club play & competitive match" },
  ski:          { name: "Skiing",       desc: "Plan ski weekends with buddies" },
  padel:        { name: "Padel",        desc: "Europe's hottest sport grows" },
  squash:       { name: "Squash",       desc: "Intense match play rated opps" },
  badminton:    { name: "Badminton",    desc: "Singles & doubles all levels" },
  climbing:     { name: "Climbing",     desc: "Find belay partners & crews" },
};

const SPORTS_DATA = Object.entries(SPORT_COPY).map(([id, c]) => ({ id, ...c }));

/**
 * Render a platform count, with no invented floor.
 *
 * `150000` and `50000` used to be the fallbacks here, so a failed or slow
 * /api/stats request rendered "150K+ Active Players" rather than nothing. A
 * number shown while the truth is unknown is a guess; we now show an em dash
 * and let the count appear when it arrives.
 */
function formatCount(value) {
  if (value === undefined || value === null) return "—";
  return value.toLocaleString();
}

const FEATURES = [
  {
    title: "Verified Community",
    desc: "Every player has skill badges, session history, and a reliability score.",
    iconBg: "#1B2A5B",
    iconColor: "#fff",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M9 12l2 2 4-4" stroke="#52E391" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="#fff" strokeWidth="1.5"/>
      </svg>
    ),
  },
  {
    title: "Book & Play",
    desc: "Send a play invite, confirm venue, and get session reminders automatically.",
    iconBg: "#52E391",
    iconColor: "#1B2A5B",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="18" rx="2" stroke="#1B2A5B" strokeWidth="2"/>
        <path d="M16 2v4M8 2v4M3 10h18" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round"/>
        <path d="M9 16l1.5 1.5L15 13" stroke="#1B2A5B" strokeWidth="2" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Compete & Improve",
    desc: "Game-specific ratings track your progress. Leagues and tournaments available.",
    iconBg: "#FF7A00",
    iconColor: "#fff",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M8 21h8M12 17v4M5 3h14l-1 8H6L5 3z" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
        <path d="M5 3C4 5 4 7 5 9M19 3c1 2 1 4 0 6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    title: "Anywhere You Play",
    desc: "50+ cities and growing. Find partners at your club or discover new venues.",
    iconBg: "#1B2A5B",
    iconColor: "#fff",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="9" stroke="#52E391" strokeWidth="1.5"/>
        <path d="M3 12h18M12 3c-3 4-3 14 0 18M12 3c3 4 3 14 0 18" stroke="#52E391" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
];

const TRUST_POINTS = [
  { icon: Shield, label: "Verified Profiles", desc: "Skill badges, session history, and reliability scores" },
  { icon: Star,   label: "Post-Session Reviews", desc: "Ratings only from completed, verified sessions" },
  { icon: Globe,  label: "50+ Cities & Growing", desc: "Fort Worth, DFW, and expanding across the US" },
];

// Decorative arc component (SVG — matches image's two-tone semicircle)
function HeroArc() {
  return (
    <div
      className="absolute right-0 top-1/2 -translate-y-1/2 w-72 h-72 sm:w-96 sm:h-96 lg:w-[480px] lg:h-[480px] pointer-events-none select-none"
      aria-hidden="true"
    >
      <svg viewBox="0 0 400 400" fill="none" className="w-full h-full opacity-80">
        {/* Green arc — top half */}
        <path
          d="M200 20 A180 180 0 0 1 380 200"
          stroke="#52E391"
          strokeWidth="48"
          strokeLinecap="round"
          fill="none"
        />
        {/* Orange arc — bottom half */}
        <path
          d="M380 200 A180 180 0 0 1 200 380"
          stroke="#FF7A00"
          strokeWidth="48"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [sportCounts, setSportCounts] = useState({});
  const [searchForm, setSearchForm] = useState({ sport: "", city: "" });
  const [recentPartners, setRecentPartners] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
    axios.get(`${API}/sports`)
      .then((r) => {
        // Store the whole record, not just the count: the card also needs
        // venue_count and `available` so it can say "no venue here yet"
        // instead of implying a sport is playable when it is not.
        const map = {};
        (r.data || []).forEach((s) => { map[s.id] = s; });
        setSportCounts(map);
      })
      .catch(() => {});
    axios.get(`${API}/partners/search?limit=3&city=Fort Worth`).then((r) => setRecentPartners(r.data.partners || [])).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchForm.sport) params.set("sport", searchForm.sport);
    if (searchForm.city) params.set("city", searchForm.city);
    navigate(`/find-partners?${params}`);
  };

  return (
    <div className="bg-white">

      {/* ── HERO ── */}
      <section className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden" data-testid="hero-section">
        {/* Background image + overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1548920168-70d61248a912?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHx0ZW5uaXMlMjBwbGF5ZXIlMjBhY3Rpb24lMjBzdW5zZXR8ZW58MHx8fHwxNzgxMDMzOTYzfDA&ixlib=rb-4.1.0&q=85"
            alt="Tennis player"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-navy/88" />
        </div>

        {/* Decorative arc — right side */}
        <HeroArc />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/20 mb-7 backdrop-blur-sm">
              <MapPin size={12} className="text-brand-green" />
              <span className="text-xs font-bold text-white tracking-widest uppercase">
                Fort Worth &amp; DFW — Now Open
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-hero font-black tracking-tight text-white leading-[0.92] mb-6">
              Find Your Perfect
              <br />
              <span className="text-brand-green">Play Partner</span>
            </h1>

            <p className="text-lg text-white/75 leading-relaxed mb-8 max-w-lg">
              Match by verified skill, book the session — not just start a chat. Tennis, pickleball, golf, table tennis, ski, and more across 50+ cities.
            </p>

            {/* Search card — WHITE */}
            <form
              onSubmit={handleSearch}
              className="bg-white rounded-2xl p-3 shadow-2xl mb-6"
              data-testid="hero-search-form"
            >
              <div className="flex flex-col sm:flex-row gap-2">
                <select
                  aria-label="Select a sport to search"
                  value={searchForm.sport}
                  onChange={(e) => setSearchForm({ ...searchForm, sport: e.target.value })}
                  className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-brand-navy text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-brand-green/40 cursor-pointer"
                  data-testid="hero-sport-select"
                >
                  <option value="">All Sports</option>
                  {SPORTS_DATA.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <div className="relative flex-1">
                  <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600" aria-hidden="true" />
                  <input
                    type="text"
                    aria-label="Search by city"
                    value={searchForm.city}
                    onChange={(e) => setSearchForm({ ...searchForm, city: e.target.value })}
                    placeholder="Fort Worth, TX"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-9 pr-4 py-3 text-brand-navy text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green/40"
                    data-testid="hero-city-input"
                  />
                </div>
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange text-white font-black rounded-xl hover:bg-brand-orange-hover transition-colors text-sm whitespace-nowrap shadow-sm"
                  data-testid="hero-search-button"
                >
                  <Search size={15} /> Find Partners
                </button>
              </div>
            </form>

            {/* Trust signals */}
            <div className="flex flex-wrap items-center gap-5 text-sm text-white/70">
              {["Free forever", "No credit card", "Verified players"].map(s => (
                <span key={s} className="flex items-center gap-1.5">
                  <CheckCircle size={14} className="text-brand-green" /> {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURE CARDS — white, rounded top, overlapping hero ── */}
      <section
        className="relative z-10 bg-white px-4 py-12 -mt-10 rounded-t-[2.5rem] shadow-[0_-8px_40px_rgba(0,0,0,0.12)]"
        data-testid="feature-cards-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map(({ title, desc, iconBg, icon }) => (
              <div key={title} className="flex flex-col items-center text-center p-5 rounded-2xl hover:bg-gray-50 transition-colors group">
                {/* Circle icon */}
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center mb-4 group-hover:scale-105 transition-transform"
                  style={{ background: iconBg }}
                >
                  {icon}
                </div>
                <h3 className="font-bold text-brand-navy mb-1.5 text-sm">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BAR — dark navy ── */}
      <section className="bg-brand-navy text-white py-14 px-4 dark-section" data-testid="stats-section">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 lg:flex lg:gap-12">
            {[
              { value: formatCount(stats?.total_players), label: "Active Players" },
              { value: formatCount(stats?.cities), label: "Cities" },
              { value: formatCount(stats?.sports), label: "Sports" },
              { value: formatCount(stats?.completed_sessions), label: "Matches Played" },
            ].map(({ value, label }) => (
              <div key={label} className="text-center lg:text-left">
                <div className="text-3xl sm:text-4xl font-hero font-black text-brand-green leading-none mb-1">{value}</div>
                <div className="text-xs text-white/50 uppercase tracking-wider font-medium">{label}</div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex-shrink-0 text-center lg:text-right">
            <p className="text-white/70 text-sm mb-3">Join thousands of players already matched.</p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-7 py-3 bg-brand-orange text-white font-bold rounded-full hover:bg-brand-orange-hover transition-colors shadow-lg"
              data-testid="stats-cta-btn"
            >
              Join Free <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — white ── */}
      <section className="py-20 px-4 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-3">Simple Process</p>
            <h2 className="text-4xl font-heading font-black text-brand-navy">Play in 3 Steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: "01", title: "Build Your Sport Profile", icon: Users,
                desc: "Set your sports, skill levels, play preferences, and availability. Each sport has its own rating system.",
              },
              {
                step: "02", title: "Get Matched by Compatibility", icon: Zap,
                desc: "Our engine considers skill level, location, schedule, format preference, and social style — not just sport.",
              },
              {
                step: "03", title: "Book the Session", icon: Star,
                desc: "Send a play invite, confirm your venue, and get reminders. Track sessions, reviews, and rematches in one place.",
              },
            ].map(({ step, title, desc, icon: Icon }) => (
              <div key={step} className="relative bg-gray-50 border border-gray-100 rounded-2xl p-6 hover:border-brand-green/30 hover:bg-green-50/30 transition-all group">
                {/* Decorative accent. This was a large low-contrast numeral
                    watermark, but text is the one thing blending cannot make
                    compliant here: even brand-green at 50% over gray-50 only
                    reaches 2.21:1 against a 3:1 large-text requirement, because
                    compositing toward white always loses contrast. A
                    non-text shape carries the same decorative weight and is
                    outside 1.4.3 entirely. */}
                <div
                  aria-hidden="true"
                  className="absolute top-4 right-4 h-16 w-16 rounded-2xl border-2 border-brand-green/25 pointer-events-none"
                />
                <div className="w-10 h-10 bg-brand-green/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-green/20 transition-colors">
                  <Icon size={20} className="text-brand-green" />
                </div>
                <h3 className="text-lg font-bold text-brand-navy mb-2">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/how-it-works" className="inline-flex items-center gap-2 text-brand-green text-sm font-semibold hover:underline">
              Learn more about how it works <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── SPORTS GRID — light gray bg ── */}
      <section className="py-20 px-4 bg-gray-50" data-testid="sports-grid-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-3">9 Sports</p>
            <h2 className="text-4xl font-heading font-black text-brand-navy">One Platform, Every Racket Sport</h2>
            <p className="mt-3 text-gray-500 max-w-xl mx-auto text-sm">Each sport has its own matching logic, skill rating system, and venue directory.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="sports-cards">
            {SPORTS_DATA.map((sport) => (
              <Link
                key={sport.id}
                to={`/sports/${sport.id}`}
                className="bg-white border border-gray-100 rounded-xl p-4 text-center hover:border-brand-green/40 hover:shadow-md transition-all group"
                data-testid={`sport-card-${sport.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-brand-navy/5 flex items-center justify-center mx-auto mb-2 group-hover:bg-brand-green/10 transition-colors">
                  <span className="text-lg">{
                    { tennis: "🎾", pickleball: "🏓", golf: "⛳", "table-tennis": "🏓", ski: "⛷️", padel: "🎾", squash: "🎯", badminton: "🏸", climbing: "🧗" }[sport.id]
                  }</span>
                </div>
                <h3 className="font-bold text-brand-navy text-sm mb-1 group-hover:text-brand-green transition-colors">{sport.name}</h3>
                <p className="text-xs text-brand-green font-medium">
                  {sportCounts[sport.id] === undefined
                    ? "—"
                    : sportCounts[sport.id].available === false
                      ? "No venue here yet"
                      : sportCounts[sport.id].venue_count > 0
                        ? `${sportCounts[sport.id].venue_count} ${sportCounts[sport.id].venue_count === 1 ? "venue" : "venues"} · ${sportCounts[sport.id].players} ${sportCounts[sport.id].players === 1 ? "player" : "players"}`
                        : `${sportCounts[sport.id].players} ${sportCounts[sport.id].players === 1 ? "player" : "players"}`}
                </p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/find-partners"
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-orange text-white font-black rounded-full hover:bg-brand-orange-hover transition-colors shadow-sm"
              data-testid="find-partners-cta"
            >
              Find Partners Near You <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── SAMPLE PARTNERS ── */}
      {recentPartners.length > 0 && (
        <section className="py-20 px-4 bg-white" data-testid="sample-partners-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-2">Active Players</p>
                <h2 className="text-3xl font-heading font-black text-brand-navy">Players Near Fort Worth</h2>
              </div>
              <Link to="/find-partners" className="text-sm text-brand-green font-semibold hover:underline flex items-center gap-1">
                See all <ChevronRight size={14} />
              </Link>
            </div>
            {/* Browsing is public; messaging is not. Say so before the visitor
                discovers it by clicking a dead button, rather than letting them
                conclude there is nobody here. */}
            {!user && (
              <p
                className="text-sm text-gray-600 bg-brand-orange/5 border border-brand-orange/20 rounded-xl px-4 py-3 mb-6"
                data-testid="anon-discovery-note"
              >
                You can browse every player here without an account.{" "}
                <Link to="/register" className="text-brand-orange font-semibold hover:underline">
                  Create a free account
                </Link>{" "}
                to message them and get matched.
              </p>
            )}
            <div className="grid md:grid-cols-3 gap-5">
              {recentPartners.map((p) => (
                <Link
                  key={p.id}
                  to={`/profile/${p.id}`}
                  className="bg-gray-50 border border-gray-100 rounded-2xl p-5 hover:border-brand-green/30 hover:shadow-sm transition-all"
                >
                  <div className="flex items-start gap-4 mb-3">
                    {p.avatar_url ? (
                      <img src={p.avatar_url} alt={p.name} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-brand-navy flex items-center justify-center text-brand-green font-black text-lg flex-shrink-0 dark-section">
                        {p.name?.[0]}
                      </div>
                    )}
                    <div>
                      <h3 className="font-bold text-brand-navy">{p.name}</h3>
                      <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                        <MapPin size={10} /> {p.city}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {(p.sports || []).slice(0, 2).map((s, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-brand-green/10 text-brand-green font-medium capitalize">{s.sport} · {s.skill_level}</span>
                    ))}
                  </div>
                  {p.bio && <p className="text-xs text-gray-500">{p.bio}</p>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FORT WORTH SPOTLIGHT ── */}
      <section className="py-20 px-4 bg-gray-50" data-testid="fort-worth-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-3">Launch City</p>
              <h2 className="text-4xl font-heading font-black text-brand-navy mb-4">
                Fort Worth's #1 Sports Partner Network
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-6">
                ActivitySports launched in Fort Worth — home to Trinity Park tennis courts, Clearfork pickleball complex, Rockwood Golf Course, and a thriving racket sports community. Find partners near TCU, West 7th, or anywhere in DFW.
              </p>
              <ul className="space-y-2 mb-8">
                {["10+ Fort Worth venues mapped", "Tennis, pickleball, golf, table tennis & more", "Free to browse, match, and message"].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-brand-navy/80">
                    <CheckCircle size={14} className="text-brand-green flex-shrink-0" /> {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/us/texas/fort-worth"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-orange text-white font-bold rounded-full hover:bg-brand-orange-hover transition-colors text-sm shadow-sm"
                data-testid="fort-worth-cta"
              >
                Explore Fort Worth <ArrowRight size={14} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1618551763300-dc7eb8ce3560?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxwaWNrbGViYWxsJTIwbWF0Y2h8ZW58MHx8fHwxNzgxMDMzOTYzfDA&ixlib=rb-4.1.0&q=85"
                alt="Pickleball match"
                className="rounded-2xl w-full h-64 object-cover shadow-md"
              />
              <div className="absolute -bottom-4 -left-4 bg-white border border-gray-100 rounded-xl p-3 shadow-xl">
                <div className="text-2xl font-hero font-black text-brand-green">312+</div>
                <div className="text-xs text-gray-400 font-medium">DFW Venues</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST ── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-widest uppercase text-brand-green mb-3">Built for Trust</p>
            <h2 className="text-4xl font-heading font-black text-brand-navy">Safety & Reliability First</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TRUST_POINTS.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-gray-50 border border-gray-100 rounded-2xl p-6 flex items-start gap-4 hover:border-brand-green/30 transition-colors">
                <div className="w-11 h-11 bg-brand-navy rounded-xl flex items-center justify-center flex-shrink-0 dark-section">
                  <Icon size={20} className="text-brand-green" />
                </div>
                <div>
                  <h3 className="font-bold text-brand-navy mb-1">{label}</h3>
                  <p className="text-sm text-gray-500">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA — dark navy ── */}
      <section className="py-20 px-4 bg-brand-navy dark-section" data-testid="final-cta-section">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-5xl font-hero font-black text-white mb-4">
            Ready to <span className="text-brand-green">Play?</span>
          </h2>
          <p className="text-white/60 mb-8">
            Join 1,800+ players. Set your profile, choose your sports, and get matched in minutes.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="px-8 py-4 bg-brand-orange text-white font-black rounded-full hover:bg-brand-orange-hover transition-colors text-lg shadow-lg"
              data-testid="final-register-cta"
            >
              Join Free Today
            </Link>
            <Link
              to="/how-it-works"
              className="px-8 py-4 bg-transparent border-2 border-white/20 text-white rounded-full hover:border-brand-green/60 hover:text-brand-green transition-colors text-lg"
            >
              How It Works
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
